
import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import ProductCard from "../shop/ProductCard";
import ProductQuickView from "./ProductQuickView";
import { useCart } from "@/contexts/CartContext";
import { Product } from "@/types/product";
import { Search, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface DatabaseProduct {
  id: string;
  name: string;
  image: string;
  points_price: number;
  description: string | null;
  stock: number;
  category_id: string | null;
  subcategory_id?: string | null;
  brand_id?: string | null;
  additional_images?: string[];
  price?: number;
  brands?: {
    name: string;
    logo: string | null;
  };
}

interface ShopProductsProps {
  categoryId?: string | null;
  subcategoryId?: string | null;
}

const ShopProducts = ({ categoryId, subcategoryId }: ShopProductsProps) => {
  const { t } = useLanguage();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [sortBy, setSortBy] = useState<string>('name');
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0
  });
  const { toast } = useToast();
  const { addToCart } = useCart();

  // Debounced search query
  const debouncedSearchQuery = useMemo(() => {
    const timeoutId = setTimeout(() => {
      return searchQuery;
    }, 300);
    return searchQuery;
  }, [searchQuery]);

  // Reset to first page when search or filters change
  useEffect(() => {
    if (pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, [searchQuery, categoryId, subcategoryId, sortBy]);

  useEffect(() => {
    fetchProducts();
  }, [categoryId, subcategoryId, sortBy, debouncedSearchQuery, pagination.page, pagination.limit]);

  const convertToProduct = (dbProduct: DatabaseProduct): Product => ({
    id: dbProduct.id,
    name: dbProduct.name,
    description: dbProduct.description || '',
    image: dbProduct.image || "/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png",
    price: dbProduct.price || 0,
    pointsPrice: dbProduct.points_price,
    brandId: dbProduct.brand_id || '',
    categoryId: dbProduct.category_id || '',
    subcategoryId: dbProduct.subcategory_id,
    stock: dbProduct.stock || 10,
    additionalImages: dbProduct.additional_images
  });

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const defaultImage = "/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png";
      
      let query = supabase
        .from('products')
        .select(`
          *,
          brands:brand_id (name, logo),
          categories:category_id (name),
          subcategories:subcategory_id (name)
        `, { count: 'exact' });
      
      // Verificar se os IDs são válidos (formato UUID)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      
      // Aplicar filtros de categoria/subcategoria
      if (subcategoryId && uuidRegex.test(subcategoryId)) {
        query = query.eq('subcategory_id', subcategoryId);
      } else if (categoryId && uuidRegex.test(categoryId)) {
        // Verificar se é categoria principal ou subcategoria
        const { data: categoryData } = await supabase
          .from('categories')
          .select('parent_id')
          .eq('id', categoryId)
          .single();
        
        if (categoryData?.parent_id) {
          // É uma subcategoria
          query = query.eq('subcategory_id', categoryId);
        } else {
          // É uma categoria principal
          query = query.eq('category_id', categoryId);
        }
      }

      // Apply search filter if provided
      if (debouncedSearchQuery && debouncedSearchQuery.trim()) {
        query = query.or(`name.ilike.%${debouncedSearchQuery}%,description.ilike.%${debouncedSearchQuery}%`);
      }

      // Aplicar ordenação
      switch (sortBy) {
        case 'points_asc':
          query = query.order('points_price', { ascending: true });
          break;
        case 'points_desc':
          query = query.order('points_price', { ascending: false });
          break;
        case 'name_asc':
          query = query.order('name', { ascending: true });
          break;
        case 'name_desc':
          query = query.order('name', { ascending: false });
          break;
        case 'newest':
          query = query.order('created_at', { ascending: false });
          break;
        default:
          query = query.order('name');
      }

      // Apply pagination - first get count to validate range
      const { count: totalCount } = await query;

      // If no products found, skip range query
      if (!totalCount || totalCount === 0) {
        setProducts([]);
        setPagination(prev => ({
          ...prev,
          total: 0,
          totalPages: 0
        }));
        setLoading(false);
        return;
      }

      const offset = (pagination.page - 1) * pagination.limit;

      // If offset is beyond total count, reset to first page
      if (offset >= totalCount) {
        setPagination(prev => ({ ...prev, page: 1 }));
        return; // fetchProducts will be called again with page 1
      }

      // Ensure end of range doesn't exceed total count
      const endRange = Math.min(offset + pagination.limit - 1, totalCount - 1);

      const { data, error, count } = await query
        .range(offset, endRange);

      if (error) throw error;

      // Convert database products to Product interface
      const convertedProducts = (data as DatabaseProduct[]).map(convertToProduct);

      setProducts(convertedProducts);

      // Update pagination info
      setPagination(prev => ({
        ...prev,
        total: count || 0,
        totalPages: Math.ceil((count || 0) / prev.limit)
      }));
    } catch (error: any) {
      console.error("Erro ao carregar produtos:", error);
      toast({
        title: t('products.loadError'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickView = (product: Product) => {
    setSelectedProduct(product);
    setIsQuickViewOpen(true);
  };

  const handleCloseQuickView = () => {
    setIsQuickViewOpen(false);
  };

  const handleAddToCart = (product: Product) => {
    addToCart(product, 1);

    toast({
      description: `${product.name} ${t('home.addedToCart')}`,
    });
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  return (
    <div className="relative z-10">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-bold text-white">
          {subcategoryId
            ? t('products.productsBySubcategory')
            : categoryId
              ? t('products.productsByCategory')
              : t('products.allProducts')}
        </h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Search Bar */}
          <div className="relative flex-1 min-w-[280px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              type="text"
              placeholder={t('products.searchProducts')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-brand-red"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* Sort Dropdown */}
          <select
            className="bg-gray-800 text-white border border-gray-700 rounded-md px-3 py-2 text-sm min-w-[160px]"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name_asc">{t('products.sortNameAsc')}</option>
            <option value="name_desc">{t('products.sortNameDesc')}</option>
            <option value="points_asc">{t('products.sortPointsAsc')}</option>
            <option value="points_desc">{t('products.sortPointsDesc')}</option>
            <option value="newest">{t('products.sortNewest')}</option>
          </select>
        </div>
      </div>

      {/* Search Results Info */}
      {!loading && (
        <div className="mb-4">
          {searchQuery && (
            <p className="text-gray-300 text-sm">
              {pagination.total} {t('products.searchResults')} "<span className="text-white font-medium">{searchQuery}</span>"
            </p>
          )}
          {!searchQuery && (
            <p className="text-gray-300 text-sm">
              {pagination.total} {t('products.productsTotal')}
            </p>
          )}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6 px-2 md:px-0">
          {Array(8).fill(0).map((_, index) => (
            <div key={index} className="animate-pulse bg-gray-800 rounded-lg h-80 md:h-96"></div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-16">
          <h3 className="text-xl text-gray-300 mb-4">{t('products.noProductsFound')}</h3>
          <Button variant="outline" onClick={() => window.history.back()}>{t('products.goBack')}</Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6 px-2 md:px-0">
          {products.map((product) => (
            <div key={product.id} className="md:hover-lift">
              <ProductCard
                product={product}
                onQuickView={() => handleQuickView(product)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && pagination.totalPages > 1 && (
        <div className="relative z-10 mt-8 mb-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-brand-darker/50 p-4 rounded-lg">
          <div className="text-gray-300 text-sm">
            {t('products.page')} {pagination.page} {t('products.of')} {pagination.totalPages} • {pagination.total} {t('products.totalProducts')}
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-center">
            {/* First page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(1)}
              disabled={pagination.page === 1}
              className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
            >
              ««
            </Button>

            {/* Previous page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
            >
              <ChevronLeft size={16} />
            </Button>

            {/* Page numbers */}
            {(() => {
              // Calculate which page numbers to show
              const pageNumbers: number[] = [];

              if (pagination.totalPages <= 5) {
                // Show all pages if 5 or fewer
                for (let i = 1; i <= pagination.totalPages; i++) {
                  pageNumbers.push(i);
                }
              } else {
                // Show 5 pages centered around current page
                let start = Math.max(1, pagination.page - 2);
                let end = Math.min(pagination.totalPages, start + 4);

                // Adjust start if we're near the end
                if (end - start < 4) {
                  start = Math.max(1, end - 4);
                }

                for (let i = start; i <= end; i++) {
                  pageNumbers.push(i);
                }
              }

              return pageNumbers.map((pageNumber) => (
                <Button
                  key={pageNumber}
                  variant={pagination.page === pageNumber ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(pageNumber)}
                  disabled={pagination.page === pageNumber}
                  className={
                    pagination.page === pageNumber
                      ? "bg-brand-red hover:bg-brand-red/90 cursor-default"
                      : "bg-gray-800 border-gray-700 text-white hover:bg-gray-700 cursor-pointer"
                  }
                >
                  {pageNumber}
                </Button>
              ));
            })()}

            {/* Next page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
            >
              <ChevronRight size={16} />
            </Button>

            {/* Last page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={pagination.page === pagination.totalPages}
              className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
            >
              »»
            </Button>
          </div>
        </div>
      )}

      <ProductQuickView
        product={selectedProduct}
        isOpen={isQuickViewOpen}
        onClose={handleCloseQuickView}
        onAddToCart={handleAddToCart}
      />
    </div>
  );
};

export default ShopProducts;
